import { EgdsSizingLengths } from './types.esm.js';

/**
 * Check string is a valid EgdsSizingLength value
 *
 * @param size {string} - size
 * @returns EgdsSizingLengths.includes(size as EgdsSizingLength)
 */
var IsEgdsSize = function (size) {
    return EgdsSizingLengths.includes(size);
};
/**
 * Check string is a valid FlexibleEgdsSizingLength value
 *
 * @param length {string} - length
 * @returns FlexibleEgdsSizingLength.includes(size as EgdsSizingLength)
 */
var IsFlexibleEgdsSize = function (length) { return length.endsWith('px'); };
/**
 * Check for deprecated "auto_fit" or "auto_fill" keys
 *
 * @param obj {UitkLayoutConditionalGridTrack} - object
 * @returns boolean
 */
var isAutoRepeatGrid = function (obj) {
    return Object.prototype.hasOwnProperty.call(obj, 'auto_fill') || Object.prototype.hasOwnProperty.call(obj, 'auto_fit');
};
/**
 * Check for "small" key
 *
 * This is used when checking values that can be applied per breakpoint, as "small" queries are
 * treated as regular values (ie: not encased in a media query when used in the CSS)
 *
 * @param key {string} - key
 * @returns boolean
 */
var isSmallKey = function (key) { return key === 'small'; };
/**
 * Outputs a css custom property for a valid EgdsSizingLength value
 *
 * @param value {EgdsSizingLength} - value
 * @returns `var(--uitk-layoutgrid-egds-size__${value})`
 */
var EgdsSizeCustomProp = function (value) { return "var(--uitk-layoutgrid-egds-size__".concat(value, ")"); };
/**
 * Outputs a pixel length, rounded to comply with the EGDS base-4 sizing rule as used to make
 * all [sizing tokens](https://egds.expedia.biz/latest/tokens/default#foundation-sizing)
 *
 * @param value {number} - number to convert
 * @param options {convertNumberToBase4Options} - options to manage rounding (set to EGDS defaults)
 */
var MakeEgdsSizingLength = function (pxValue, options) {
    if (options === void 0) { options = {
        step: 4,
        exceptions: [1, 2, 6, 14, 18]
    }; }
    var UnitlessValue = Number(pxValue.replace('px', ''));
    var isException = options.exceptions.includes(UnitlessValue);
    return isException ? pxValue : "".concat(Math.round(UnitlessValue / options.step) * options.step, "px");
};
/**
 * Returns a track item, checking if it is an EGDS size that needs to be processed
 *
 * @param item {any} - track item to return
 */
var MaketrackItem = function (item) {
    var trackItem;
    if (IsEgdsSize(item)) {
        trackItem = EgdsSizeCustomProp(item);
    }
    else if (IsFlexibleEgdsSize(item)) {
        trackItem = MakeEgdsSizingLength(item);
    }
    else {
        trackItem = item;
    }
    return trackItem;
};
/**
 * Outputs a css fit-content() function
 *
 * @param length {EgdsSizingLength | FlexibleEgdsSizingLength} - length
 * @returns {`fit-content(${typeof fitContentValue})`} `fit-content(length)`
 */
var FitContent = function (length) {
    var fitContentValue = MaketrackItem(length);
    var fitcontentValue = "fit-content(".concat(fitContentValue, ")");
    return fitcontentValue;
};
/**
 * Outputs a css minmax() function
 *
 * @param min {UitkLayoutGridLength} - min value
 * @param max {UitkLayoutGridLength} - max value
 * @returns {`minmax(${typeof minValue}, ${typeof maxValue})`} `minmax(min, max)`
 */
var MinMax = function (min, max) {
    var minValue = MaketrackItem(min);
    var maxValue = MaketrackItem(max);
    var minmaxValue = "minmax(".concat(minValue, ", ").concat(maxValue, ")");
    return minmaxValue;
};
/**
 * Outputs a css repeat() function
 *
 * @param repeatType {number | 'auto-fit' | 'auto-fill'} - repeat type
 * @param track {UitkLayoutGridLength | ReturnType<typeof MinMax> | ReturnType<typeof FitContent>} - track to repeat
 * @returns {`repeat(${typeof repeatType}, ${typeof track})`} `repeat(repeatType, track)`
 */
var Repeat = function (repeatType, track) {
    // eslint-disable-next-line no-nested-ternary
    var trackValue = MaketrackItem(track);
    var repeatValue = "repeat(".concat(repeatType, ", ").concat(trackValue, ")");
    return repeatValue;
};

export { EgdsSizeCustomProp, FitContent, IsEgdsSize, IsFlexibleEgdsSize, MakeEgdsSizingLength, MaketrackItem, MinMax, Repeat, isAutoRepeatGrid, isSmallKey };
